import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type {
  TimeControl,
  TournamentSystem,
} from '@swiss-magic/shared-types';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { TournamentFormController } from './useTournamentFormController';

// Game + game-specific format options. Game choice is locked once
// the tournament leaves draft (round 1 generated). System choice is
// locked once the tournament moves to registration_closed or beyond.
// Time control and match length are conditional on the game.

function systemLabel(system: string, t: TFunction): string {
  const key = `tournament_form.system.${system}`;
  const translated = t(key);
  return translated === key ? system : translated;
}

function timeControlLabel(tc: string, t: TFunction): string {
  const slug = tc.toLowerCase().replace(/\s+/g, '_');
  const key = `tournament_form.time_control_options.${slug}`;
  const translated = t(key);
  return translated === key ? tc : translated;
}

interface Props {
  form: TournamentFormController;
  systemLocked: boolean;
}

export function GameSection({ form, systemLocked }: Props) {
  const { t } = useTranslation('director');

  return (
    <>
      <div className="space-y-2">
        <Label>{t('tournament_form.game_label')}</Label>
        <Select
          value={form.game}
          disabled={form.isEdit && form.tournamentStatus !== 'draft'}
          onValueChange={form.handleGameChange}
        >
          <SelectTrigger data-testid="game-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="chess">{t('tournament_form.game_options.chess')}</SelectItem>
            <SelectItem value="backgammon">{t('tournament_form.game_options.backgammon')}</SelectItem>
            <SelectItem value="other">{t('tournament_form.game_options.other')}</SelectItem>
          </SelectContent>
        </Select>
        {form.game === 'other' && (
          <Input
            data-testid="game-name-input"
            value={form.gameName}
            onChange={(e) => form.setGameName(e.target.value)}
            placeholder={t('tournament_form.game_name_placeholder')}
          />
        )}
      </div>

      {form.gameConfig.timeControls.length > 0 && (
        <div className="space-y-2">
          <Label>{t('tournament_form.time_control_label')}</Label>
          <Select
            value={form.timeControl}
            onValueChange={(v) => form.setTimeControl(v as TimeControl)}
          >
            <SelectTrigger data-testid="time-control-select">
              <SelectValue placeholder={t('tournament_form.time_control_placeholder')} />
            </SelectTrigger>
            <SelectContent>
              {form.gameConfig.timeControls.map((tc) => (
                <SelectItem key={tc} value={tc}>
                  {timeControlLabel(tc, t)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Match length applies to backgammon and to many 'other'
          games (any best-of-N format). Chess uses round count
          alone, so it's hidden there. */}
      {(form.game === 'backgammon' || form.game === 'other') && (
        <div className="space-y-2">
          <Label htmlFor="matchLength">{t('tournament_form.match_length_label')}</Label>
          <Input
            id="matchLength"
            data-testid="match-length-input"
            type="number"
            min="1"
            value={form.matchLength}
            onChange={(e) => form.setMatchLength(e.target.value)}
            placeholder={t('tournament_form.match_length_placeholder')}
          />
        </div>
      )}

      <div className="space-y-2">
        <Label>{t('tournament_form.system_label')}</Label>
        <Select
          value={form.system}
          disabled={systemLocked}
          onValueChange={(v) => form.handleSystemChange(v as TournamentSystem)}
        >
          <SelectTrigger data-testid="system-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {form.gameConfig.tournamentSystems.map((s) => (
              <SelectItem key={s} value={s}>
                {systemLabel(s, t)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {(form.system === 'round_robin' ||
          form.system === 'double_round_robin') && (
          <p className="text-sm text-muted-foreground">
            {t('tournament_form.round_robin_max_note')}
          </p>
        )}
      </div>
    </>
  );
}
